import React, { useMemo } from "react";
import {
  ITag,
  Stack,
  Label,
  TooltipHost,
  Icon,
  Link,
  Text,
  MessageBar,
  MessageBarType,
} from "@fluentui/react";
import { styles } from "./experimentDetailsForm.styles";
import {
  BaseTextField,
  BaseTagField,
  BaseDropdownField,
} from "../FormComponents";
import BaseTagDropdownField from "../FormComponents/BaseTagDropdownField";
import { EnvironmentFilters } from "../../../Types";
import useAppInsights from "../../../Hooks/UseAppInsights";
import { NavType } from "../../../Shared/AppInsights";
import { MAX_TRAFFIC_FILTER_COUNT } from "../../../Shared/shared.consts";

interface AdvancedFiltersProps {
  // Tenant Filters
  /**
   * Operator for tenant filtering
   */
  tenantFiltersOperator: "IN" | "NOTIN";
  /**
   * List of tenant IDs to filter by
   */
  tenantFiltersList: ITag[];
  /**
   * Callback when tenant operator changes
   */
  onTenantFiltersOperatorChange: (operator: "IN" | "NOTIN") => void;
  /**
   * Callback when tenant list changes
   */
  onTenantFiltersListChange: (tags: ITag[]) => void;
  // Extension Version Filters
  /**
   * Comparison operator for extension version filtering
   */
  extensionVersionFiltersOperator: string;
  /**
   * Version string to compare against
   */
  extensionVersionFiltersVersion: string;
  /**
   * Callback when version operator changes
   */
  onExtensionVersionFiltersOperatorChange: (operator: string) => void;
  /**
   * Callback when version string changes
   */
  onExtensionVersionFiltersVersionChange: (version: string) => void;

  // Environment Filters
  /**
   * Environment filters organized by deployment stage
   */
  environmentFilters: EnvironmentFilters;

  /**
   * Callback when environment filters change for a specific stage
   */
  onEnvironmentFiltersChange: (
    stage: "RC" | "MPAC" | "Prod",
    environments: string[],
  ) => void;

  // Common props
  /**
   * When true, forms are editable. When false, forms are read-only.
   */
  isEdit: boolean;
  /**
   * Validation errors keyed by field name
   */
  errors?: Record<string, string[]>;
}

/**
 * Advanced filtering interface for precise experiment audience targeting.
 * Provides three main filter categories with dedicated UI sections and validation.
 *
 * Filter Categories:
 * 1. Tenant Filters: Include/exclude specific tenants using IN/NOTIN operators
 * 2. Extension Version Filters: Target specific extension versions using comparison operators
 * 3. Environment Filters: Override default environment mappings for RC/MPAC/Prod stages
 */
const AdvancedFilters: React.FC<AdvancedFiltersProps> = ({
  tenantFiltersOperator,
  tenantFiltersList,
  onTenantFiltersOperatorChange,
  onTenantFiltersListChange,
  extensionVersionFiltersOperator,
  extensionVersionFiltersVersion,
  onExtensionVersionFiltersOperatorChange,
  onExtensionVersionFiltersVersionChange,
  environmentFilters,
  onEnvironmentFiltersChange,
  isEdit,
  errors = {},
}) => {
  const { trackNavigationEvent } = useAppInsights("AdvancedFilters");

  const trafficFiltersCount = useMemo(() => {
    const tenantCount = tenantFiltersList.length;
    const environmentCount = Object.values(environmentFilters).reduce(
      (sum, envList) => sum + envList.length,
      0,
    );
    const extensionVersionCount = extensionVersionFiltersVersion ? 1 : 0;
    return tenantCount + environmentCount + extensionVersionCount;
  }, [
    environmentFilters,
    extensionVersionFiltersVersion,
    tenantFiltersList.length,
  ]);

  return (
    <Stack tokens={{ childrenGap: 20 }}>
      {trafficFiltersCount > MAX_TRAFFIC_FILTER_COUNT && (
        <MessageBar messageBarType={MessageBarType.error} isMultiline={true}>
          {`Total traffic filters (${trafficFiltersCount}) exceeds ${MAX_TRAFFIC_FILTER_COUNT}. Excessive filters can lead to performance issues. Please reduce the number of filters or deploy experiment at your own risk.`}
        </MessageBar>
      )}
      {/* Tenant Filter Section */}
      <Stack tokens={{ childrenGap: 8 }}>
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
          <Label
            style={{ fontSize: "16px", fontWeight: 600, color: "#323130" }}
          >
            Tenant Filter
          </Label>
          <TooltipHost content="Use tenant filtering to target specific tenants for controlled rollouts across any environment.">
            <Icon iconName="Info" className={styles.experimentDetailsTooltip} />
          </TooltipHost>
        </Stack>

        <Stack
          tokens={{ childrenGap: 16 }}
          style={{
            padding: "16px",
            border: "1px solid #d2d0ce",
            borderRadius: "2px",
            backgroundColor: "#fdfdfd",
          }}
        >
          <BaseDropdownField
            fieldKey="tenantFiltersOperator"
            label="Operator"
            value={tenantFiltersOperator}
            options={tenantOperatorOptions}
            isRequired={false}
            isEdit={isEdit}
            placeholder="Select operator..."
            onChange={(field, value) =>
              onTenantFiltersOperatorChange(value as "IN" | "NOTIN")
            }
            errorMessage={errors.tenantFiltersOperator || undefined}
            grow
          />
          <BaseTagField
            fieldKey="tenantFiltersList"
            label="Tenant List"
            selectedItems={tenantFiltersList}
            isRequired={false}
            isEdit={isEdit}
            placeholder="Add tenant..."
            onChange={(field, items) => onTenantFiltersListChange(items)}
            errorMessage={errors.tenantFiltersList || undefined}
            enableLoadMore={true}
            initialVisibleItems={MAX_TRAFFIC_FILTER_COUNT}
            loadMoreItemsCount={MAX_TRAFFIC_FILTER_COUNT}
            grow
          />
        </Stack>
      </Stack>

      {/* Extension Version Filter Section */}
      <Stack tokens={{ childrenGap: 8 }}>
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
          <Label
            style={{ fontSize: "16px", fontWeight: 600, color: "#323130" }}
          >
            Extension Version Filter
          </Label>
          <TooltipHost content="Filter by extension version using a comparison operator and version string.">
            <Icon iconName="Info" className={styles.experimentDetailsTooltip} />
          </TooltipHost>
        </Stack>

        <Stack
          tokens={{ childrenGap: 16 }}
          style={{
            padding: "16px",
            border: "1px solid #d2d0ce",
            borderRadius: "2px",
            backgroundColor: "#fdfdfd",
          }}
        >
          <Stack horizontal tokens={{ childrenGap: 16 }} verticalAlign="start">
            <Stack grow>
              <BaseDropdownField
                fieldKey="extensionVersionFiltersOperator"
                label="Operator"
                value={extensionVersionFiltersOperator}
                options={versionOperatorOptions}
                isRequired={false}
                isEdit={isEdit}
                placeholder="Select operator..."
                onChange={(field, value) =>
                  onExtensionVersionFiltersOperatorChange(value || "")
                }
                errorMessage={
                  errors.extensionVersionFiltersOperator || undefined
                }
                grow
              />
            </Stack>
            <Stack grow>
              <BaseTextField
                fieldKey="extensionVersionFiltersVersion"
                label="Extension Version"
                value={extensionVersionFiltersVersion}
                isRequired={false}
                isEdit={isEdit}
                placeholder="e.g., 1.0.0.2"
                onChange={(field, value) =>
                  onExtensionVersionFiltersVersionChange(value || "")
                }
                errorMessage={
                  errors.extensionVersionFiltersVersion || undefined
                }
                grow
              />
            </Stack>
          </Stack>
        </Stack>
      </Stack>

      {/* Environment Filters Section */}
      <Stack tokens={{ childrenGap: 8 }}>
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
          <Label
            style={{ fontSize: "16px", fontWeight: 600, color: "#323130" }}
          >
            Environment Domain Filters
          </Label>
          <TooltipHost content="Customize which specific Azure Portal environments an experiment targets for each deployment stage. If not specified, default environment mappings will be used automatically.">
            <Icon iconName="Info" className={styles.experimentDetailsTooltip} />
          </TooltipHost>
        </Stack>

        <Stack
          tokens={{ childrenGap: 16 }}
          style={{
            padding: "16px",
            border: "1px solid #d2d0ce",
            borderRadius: "2px",
            backgroundColor: "#fdfdfd",
          }}
        >
          <Stack horizontalAlign="start">
            <Text
              style={{
                fontStyle: "italic",
              }}
            >
              Only specify environments here if you want to override the system
              defaults. See{" "}
              <Link
                target="'_blank"
                href="https://eng.ms/docs/products/azure-portal-framework-ibizafx/experimentation/ev2configurationspec#allowed-environment-values"
                onClick={() => {
                  trackNavigationEvent({
                    destination:
                      "https://eng.ms/docs/products/azure-portal-framework-ibizafx/experimentation/ev2configurationspec#allowed-environment-values",
                    navType: NavType.External,
                  });
                }}
              >
                ev2 configuration spec documentation
              </Link>{" "}
              for additional mappings.
            </Text>
          </Stack>

          <BaseTagDropdownField
            fieldKey="environmentFilters.RC"
            label="RC Environments"
            selectedOptions={environmentFilters.RC || []}
            options={environmentOptions.rc.options}
            onChange={(_, items) => onEnvironmentFiltersChange("RC", items)}
            isEdit={isEdit}
            errorMessage={errors.environmentFiltersRC || undefined}
            grow
            secondaryContentRenderer={(option) =>
              environmentOptions.rc.secondaryContent[option] || ""
            }
          />
          <BaseTagDropdownField
            fieldKey="environmentFilters.MPAC"
            label="MPAC Environments"
            selectedOptions={environmentFilters.MPAC || []}
            options={environmentOptions.mpac.options}
            onChange={(_, items) => onEnvironmentFiltersChange("MPAC", items)}
            isEdit={isEdit}
            errorMessage={errors.environmentFiltersMPAC || undefined}
            grow
            secondaryContentRenderer={(option) =>
              environmentOptions.mpac.secondaryContent[option] || ""
            }
          />
          <BaseTagDropdownField
            fieldKey="environmentFilters.Prod"
            label="Prod Environments"
            selectedOptions={environmentFilters.Prod || []}
            options={environmentOptions.prod.options}
            onChange={(_, items) => onEnvironmentFiltersChange("Prod", items)}
            isEdit={isEdit}
            errorMessage={errors.environmentFiltersProd || undefined}
            grow
            secondaryContentRenderer={(option) =>
              environmentOptions.prod.secondaryContent[option] || ""
            }
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

const tenantOperatorOptions = [
  { key: "IN", text: "IN" },
  { key: "NOTIN", text: "NOTIN" },
];

const versionOperatorOptions = [
  { key: ">=", text: ">=" },
  { key: "<=", text: "<=" },
  { key: "==", text: "==" },
  { key: "!=", text: "!=" },
  { key: "<", text: "<" },
  { key: ">", text: ">" },
];

const environmentOptions: Record<
  string,
  { options: string[]; secondaryContent: Record<string, string> }
> = {
  rc: {
    options: ["rc", "df"],
    secondaryContent: {
      rc: "rc.portal.azure.com",
      df: "df.onecloud.azure-test.net",
    },
  },
  mpac: {
    options: ["mpac", "preview"],
    secondaryContent: {
      mpac: "ms.portal.azure.com, canary-ms.portal.azure.com",
      preview: "preview.portal.azure.com",
    },
  },
  prod: {
    options: [
      "prod",
      "entra-prod",
      "intune-prod",
      "other-prod",
      "engagehub-prod",
      "vlcentral-prod",
    ],
    secondaryContent: {
      prod: "portal.azure.com, canary.portal.azure.com",
      "entra-prod": "entra.microsoft.com, canary.entra.microsoft.com",
      "intune-prod":
        "endpoint.microsoft.com, intune.microsoft.com, intuneeducation.microsoft.com, devicemanagement.portal.azure.com, devicemanagement.microsoft.com",
      "other-prod": "Various production portals",
      "engagehub-prod": "engagehub.microsoft.com, engagecenter.microsoft.com",
      "vlcentral-prod": "vlcentral.microsoft.com, vlcentral.portal.azure.com",
    },
  },
};

export default AdvancedFilters;
